import {
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DxDataGridComponent, DxListComponent } from 'devextreme-angular';
import { TranslateService } from '@ngx-translate/core';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { LoadingService } from '../../../../services/loading.service';
import { TrainingService } from '../../../../services/training.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';
import { PersonService } from 'src/app/services/person.service';
import { TableColumn } from 'src/app/models/table-column.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { TimeService } from 'src/app/services/time.service';
import { RoleModel } from 'src/app/models/person/role.model';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { TrainingLocationTypeModel } from 'src/app/models/training/training-location-type.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { TrainingFullModel } from 'src/app/models/training/training-full.model';
import { ProfileModel } from 'src/app/models/profile.model';
import { PersonFullModel } from 'src/app/models/person/person-full.model';
import { TrainingMeetingAttendeePersonModel } from 'src/app/models/training/training-meeting-attendee-person.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';

@Component({
  selector: 'app-attendee-list',
  templateUrl: './attendee-list.component.html',
  styleUrl: './attendee-list.component.scss',
  standalone: false
})
export class AttendeeListComponent implements OnInit {
  @ViewChild('dataGrid', { static: false }) dataGrid!: DxDataGridComponent;

  @ViewChild('list', { static: true })
  list!: DxListComponent;

  selectedPersons: Attendee[] = [];
  inactivePersons: Attendee[] = [];
  personsWithESignature: Attendee[] = [];

  @Input()
  trainingId!: string;

  store!: CustomStore;
  dataSource!: DataSource;

  personsColumns: TableColumn[] = [];
  training: TrainingFullModel | null = null;
  isMeeting: boolean = false;

  selectedActivatePersonId: string | null = null;
  selectedCreateProfilePerson: ProfileModel | null = null;
  selectedMergePerson: ProfileModel | null = null;

  showActivateUserModal: boolean = false;
  showActivateAllUsersModal: boolean = false;
  showAddParticipantsModal: boolean = false;
  showCreateProfileModal: boolean = false;
  showMergePersonModal: boolean = false;

  additionalParticipants: Attendee[] = [];
  deletedAttendeeKeys: string[] = [];

  meetingAttendees: TrainingMeetingAttendeePersonModel[] | null = null;

  private iddTimeChanges = new Map<string, number>();

  get selectedPersonIds(): string[] {
    return this.selectedPersons.map((person) => person.id);
  }

  get isAllPersonsValid(): boolean {
    return this.selectedPersons.every(
      person => !person.isUnknownUser && !person.inactive
    );
  }

  getAttendeeKey(p: Attendee): string {
    return p.isUnknownUser ? p.eventMemberUnknownId! : p.id;
  }

  constructor(
    private loadingService: LoadingService,
    private trainingService: TrainingService,
    private personService: PersonService,
    private translateService: TranslateService,
  ) {}

  get isIDDTrainingType(): boolean {
    if(!this.training) return false;

    return this.training.training.trainingType == TrainingTypeModel.IDD;
  }

  get trainingIDDTime(): number {
    return this.training?.training.iddTimeMinutes ?? 0;
  }
  
  ngOnInit(): void {
    this.loadingService
      .load(
        this.trainingService.getTraining(this.trainingId)
      ).subscribe((training) => {
        this.training = training;
        const locationType = this.training.training.trainingLocation.type;
        this.isMeeting = locationType !== TrainingLocationTypeModel.Address && locationType !== TrainingLocationTypeModel.Web;

        this.personsColumns = [
          {
            caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
            dataField: 'lastName',
            cssClass: 'width-200',
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
            dataField: 'firstName',
            cssClass: 'width-200',
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
            dataField: 'email',
            cssClass: 'width-150',
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.ROLE'),
            dataField: 'role.name',
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
            dataField: 'location.name',
            cssClass: 'width-150',
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.DURATION'),
            dataField: 'attendanceDurationMinutes',
            cssClass: 'width-100',
            visible: this.isMeeting,
            calculateDisplayValue: (data: Attendee): string => {
              return data.attendanceDurationMinutes ? 
                TimeService.getHoursAndMinutesDisplayStringFromIddTimeMinutes(
                  data.attendanceDurationMinutes
                ) : '';
            },
            calculateSortValue: (data: any) => {
              return data.attendanceDurationMinutes;
            },
            headerFilter: (data: any) => {
              data.dataSource.postProcess = (results: any) => {
                const iddTimeMinutes = results.map((result: any) => result.value);
                results.splice(0, results.length);
                for (let iddTime of iddTimeMinutes) {
                  results.push({
                    text: TimeService.getHoursAndMinutesDisplayStringFromIddTimeMinutes(
                      iddTime
                    ),
                    value: iddTime,
                  });
                }
              };
            },
          },
          {
            caption: this.translateService.instant('COMMON.FIELDS.TRAINING-GROUPS'),
            dataField: 'trainingGroups',
            cssClass: 'width-350',
            headerFilter: (data: any) => {
              data.dataSource.postProcess = (results: any) => {
                const trainingGroupNames = results.reduce(
                  (a: any, b: any) => a.concat(b.value),
                  []
                );
                const parsedTrainingGroupNames: string[] = [];
                trainingGroupNames.forEach((group: string) => {
                  if (!group || !group.includes(', ')) {
                    parsedTrainingGroupNames.push(group);
                  } else if (group.includes(', ')) {
                    const groups = group.split(', ');
                    parsedTrainingGroupNames.push(...groups);
                  }
                });
                const uniqueTrainingGroupNames = [
                  ...new Set(parsedTrainingGroupNames),
                ];

                results.splice(0, results.length);
                uniqueTrainingGroupNames.forEach((tgName) => {
                  if (tgName) {
                    results.push({
                      text: tgName,
                      value: tgName,
                    });
                  } else {
                    results.push({
                      text: '(Blanks)',
                      value: null,
                    });
                  }
                });
                return results;
              };
            },
            filterExpression: (value: any) => {
              function getTrainingGroupsFilterString(rowData: Attendee) {
                const groups = rowData.trainingGroups.map((tg) => tg.name);
                return !!groups.join(',') ? groups.join(',') + ',' : 'empty';
              }

              return [
                [
                  getTrainingGroupsFilterString,
                  'contains',
                  value ? value + ',' : 'empty',
                ],
              ];
            },
            cellValue: (data: Attendee | any): string => {
              return data.trainingGroups.map((tg: any) => tg.name).join(', ');
            },
          },
        ];

        this.loadAttendees();
      }
    );
  }

  loadAttendees() {
    const participantIds = this.additionalParticipants.map(p => p.id);

    const meetingAttendees$ = this.meetingAttendees !== null 
      ? of(this.meetingAttendees)
      : (
        this.isMeeting
          ? this.trainingService
              .getTrainingMeetingAttendees(this.trainingId)
              .pipe(catchError(() => of(null)))
          : of(null)
      ); // fetch meeting attendees only once

    const request$ = forkJoin({
      eventMembers: !this.isMeeting
        ? this.trainingService.getTrainingMemberAttendees(this.trainingId)
        : of(null),

      meetingAttendees: meetingAttendees$,

      additionalParticipants: participantIds.length === 0
        ? of([])
        : this.personService.getPersonsWithEventMember(
            this.trainingId,
            participantIds
          )
    });

    this.loadingService.load(request$)
      .subscribe(({ eventMembers, meetingAttendees, additionalParticipants }) => {
        const trainingIDDTime = this.trainingIDDTime;

        if (!this.isMeeting) {
          // Only eventMembers
          const persons: Attendee[] = eventMembers!.map(em => {
            const iddTimeMinutes = em.iddTimeMinutes ?? trainingIDDTime;

            return {
              ...em.person.person,
              eSignature: em.eSignature,
              trainingGroups: em.person.trainingGroups,
              isUnknownUser: em.isUnknown,
              eventMemberUnknownId: em.eventMemberUnknownId,
              isManuallyAdded: em.isManuallyAdded,
              iddTimeMinutes,
              initialIddTimeMinutes: iddTimeMinutes
            };
          });

          this.selectedPersons = persons
            .filter(person => !this.deletedAttendeeKeys.includes(this.getAttendeeKey(person)));

          this.personsWithESignature = persons
            .filter(person => !!person.eSignature);
        } else {
          // Only meetingAttendees
          this.meetingAttendees = meetingAttendees;
          this.selectedPersons = meetingAttendees!.map(m => {
            const iddTimeMinutes = m.iddTimeMinutes ?? trainingIDDTime;

            return {
              ...m.person.person,
              isUnknownUser: m.isUnknownUser,
              eventMemberUnknownId: m.eventMemberUnknownId,
              eSignature: m.eSignature,
              trainingGroups: m.person.trainingGroups,
              isManuallyAdded: m.isManuallyAdded,
              iddTimeMinutes,
              initialIddTimeMinutes: iddTimeMinutes,
              attendanceDurationMinutes: m.attendanceDurationMinutes,
            };
          }).filter(person =>
            !this.deletedAttendeeKeys.includes(this.getAttendeeKey(person))
          );
        }

        this.inactivePersons = this.selectedPersons.filter(p => p.inactive);

        const attendees: Attendee[] = additionalParticipants.map(p => {
          return this.mapToAttendee(
            p.person.person, p.person.trainingGroups, p.eventMember?.duration, p.eventMember?.eSignature
          )
        });
        this.additionalParticipants = attendees;

        this.selectedPersons.push(...this.additionalParticipants);
        this.sortSelectedPersons();

        this.selectedPersons.forEach(p => {
          if (p.id && this.iddTimeChanges.has(p.id)) {
            p.iddTimeMinutes = this.iddTimeChanges.get(p.id)!;
          }
        });
      });
  }

  sortSelectedPersons() {
    this.selectedPersons.sort((a, b) => {
      // unknown users come first
      if (!!a.isUnknownUser !== !!b.isUnknownUser) {
        return a.isUnknownUser ? -1 : 1;
      }

      // inactive users next
      if (!!a.inactive !== !!b.inactive) {
        return a.inactive ? -1 : 1;
      }

      // sort by lastName
      const lastNameA = (a.lastName || '').toLocaleLowerCase();
      const lastNameB = (b.lastName || '').toLocaleLowerCase();

      if (lastNameA < lastNameB) return -1;
      if (lastNameA > lastNameB) return 1;

      return 0;
    });
  }

  public allParticipantsWithESignature(): boolean {
    if(this.selectedPersons.length == 0) return false;
    return this.selectedPersons.every(p => p.eSignature);
  }

  onTableSearch(event: any): void {
    this.dataGrid.instance.searchByText(event.value);
  } 

  onMergePerson(person: Attendee) {
    this.selectedMergePerson = {
      ...person
    } as ProfileModel;
    this.showMergePersonModal = true;
  }

  onMergedPerson(person: PersonModel) {
    if(this.selectedMergePerson) {
      const index = this.additionalParticipants.findIndex(
        p => p.email === this.selectedMergePerson!.email
      );
      const attendee = this.mapToAttendee(
        person, [], this.selectedMergePerson.attendanceDurationMinutes, null,this.selectedMergePerson.iddTimeMinutes
      );

      if (index > -1) { // additional participant was merged
        this.additionalParticipants[index] = attendee
      } else if(this.isMeeting) { // add to additional for meeting
        this.additionalParticipants.push(attendee);
      }

      this.iddTimeChanges.delete(this.selectedMergePerson.id);
      this.iddTimeChanges.set(person.id, this.selectedMergePerson.iddTimeMinutes);

      this.removeAttendeeFromState(this.selectedMergePerson as Attendee);

      // Target person may have previously been marked as deleted.
      // After merging into it, it must be visible again.
      this.deletedAttendeeKeys = this.deletedAttendeeKeys.filter(
        id => id !== person.id
      );

      this.selectedMergePerson = null;  
    }
    
    this.showMergePersonModal = false;
    this.loadAttendees();
  }

  onMergePersonClosed() {
    this.selectedMergePerson = null;
    this.showMergePersonModal = false;
  }

  onDeletePerson(person: Attendee) {
    if (person.isManuallyAdded) {
      this.loadingService.load(
         this.trainingService
          .removeAdditionalParticipant(this.trainingId, person.id)
      ).subscribe(() => {
        this.removeAttendeeFromState(person);
      });

      return;
    }

    this.removeAttendeeFromState(person);
  }

  onActivateUser(person: Attendee) {
    this.selectedActivatePersonId = person.id;
    this.showActivateUserModal = true;
  }

  onUserActivated() {
    this.showActivateUserModal = false;
    this.selectedActivatePersonId = null;
    this.loadAttendees();
  }

  onActivateUserClosed() {
    this.showActivateUserModal = false;
    this.selectedActivatePersonId = null;
  }


  onActivateAllUsers() {
    this.showActivateAllUsersModal = true;
  }

  onAllUsersActivated() {
    this.showActivateAllUsersModal = false;
    this.loadAttendees();
  }

  onActivateAllUsersClosed() {
    this.showActivateAllUsersModal = false;
  }


  onCreateProfile(person: Attendee) {
    this.selectedCreateProfilePerson = {
      ...person
    } as ProfileModel;

    this.showCreateProfileModal = true;
  }

  onCreatedProfile(person: PersonFullModel) {
    if(!this.selectedCreateProfilePerson) return;

    if(this.isMeeting) {
      
      if(this.selectedCreateProfilePerson.email !== person.person.email) {
        // if email has changed, add new to additional and remove old
        const attendee = this.mapToAttendee(
          person.person, person.trainingGroups, this.selectedCreateProfilePerson.attendanceDurationMinutes, null, this.selectedCreateProfilePerson.iddTimeMinutes
        );
        this.additionalParticipants.push(attendee);
        
        this.removeAttendeeFromState(this.selectedCreateProfilePerson as Attendee);
      } 
      else if(this.meetingAttendees) {
        const index = this.meetingAttendees.findIndex(a =>
          a.person.person.email === this.selectedCreateProfilePerson!.email
        );

        if (index !== -1) {
          // update meeting attendee
          this.meetingAttendees[index] = {
            ...this.meetingAttendees[index],
            person: person,
            isUnknownUser: false,
            eventMemberUnknownId: this.selectedCreateProfilePerson.eventMemberUnknownId ?? null,
            eSignature: this.selectedCreateProfilePerson.eSignature ?? null,
            attendanceDurationMinutes: this.selectedCreateProfilePerson.attendanceDurationMinutes,
          };
        }
      }

      this.iddTimeChanges.delete(this.selectedCreateProfilePerson.id);
      this.iddTimeChanges.set(person.person.id, this.selectedCreateProfilePerson.iddTimeMinutes);
    }

    this.showCreateProfileModal = false;
    this.selectedCreateProfilePerson = null;
    this.loadAttendees();
  }

  onCreateProfileClosed() {
    this.showCreateProfileModal = false;
    this.selectedCreateProfilePerson = null;
  }


  onAddParticipants() {
    this.showAddParticipantsModal = true;
  }

  onAddedParticipants(participants: PersonModel[]) {
    const participantIds = participants.map(p => p.id);

    if (participantIds.length === 0) {
      this.showAddParticipantsModal = false;
      return;
    }

    this.loadingService
      .load(
        this.trainingService.addAdditionalParticipants(
          this.trainingId,
          participantIds
        ).pipe(
          switchMap(() => 
            this.personService.getPersonsByIds(participantIds)
          )
        )
      )
      .subscribe((persons) => {
        const attendees: Attendee[] = persons.map(p => ({
          ...p.person,
          trainingGroups: p.trainingGroups,
          eSignature: this.personsWithESignature.find(
            pESignature => pESignature.id == p.person.id
          )?.eSignature,
          iddTimeMinutes: this.trainingIDDTime,
          initialIddTimeMinutes: this.trainingIDDTime,
          isManuallyAdded: true
        }));

        attendees.forEach(attendee => {
          if (!this.additionalParticipants.some(p => p.id === attendee.id)) {
            this.additionalParticipants.push(attendee);
          }

          if (!this.selectedPersons.some(p => p.id === attendee.id)) {
            this.selectedPersons.push(attendee);
          }
        });

        this.sortSelectedPersons();
        this.showAddParticipantsModal = false;
      });
  }

  onAddParticipantsClosed() {
    this.showAddParticipantsModal = false;
  }

  private mapToAttendee(
    person: PersonModel,
    trainingGroups: TrainingGroupModel[],
    attendanceDurationMinutes?: number,
    eSignature?: string | null,
    iddTimeMinutes?: number,
  ): Attendee {
    const resolvedIddTime = iddTimeMinutes ?? this.trainingIDDTime;

    return {
      ...person,
      trainingGroups,
      eSignature,
      attendanceDurationMinutes,
      iddTimeMinutes: resolvedIddTime,
      initialIddTimeMinutes: resolvedIddTime
    } as Attendee;
  }

  private removeAttendeeFromState(person: Attendee) {
    if (this.meetingAttendees) {
      this.meetingAttendees = this.meetingAttendees.filter(
        attendee => attendee.person.person.id !== person.id
      );
    }

    const key = this.getAttendeeKey(person);
    if (key && !this.deletedAttendeeKeys.includes(key)) {
      this.deletedAttendeeKeys.push(key);
    }

    this.selectedPersons = this.selectedPersons.filter(p => p.id !== person.id);
    this.additionalParticipants = this.additionalParticipants.filter(p => p.id !== person.id);
    this.inactivePersons = this.inactivePersons.filter(p => p.id !== person.id);
  }

  onIddTimeChanged(person: any, event: any): void {
    const newValue = Math.floor(event.value ?? 0);

    person.iddTimeMinutes = newValue;
    this.iddTimeChanges.set(person.id, newValue);
  }

  public hasAnyIddTimeChanged(): boolean {
    if (!this.isIDDTrainingType) return false;

    return this.selectedPersons.some(person =>
      person.iddTimeMinutes !== person.initialIddTimeMinutes
    );
  }
}

interface Attendee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  inactive?: boolean;
  role?: RoleModel;
  location?: OrganisationUnitModel;
  eSignature?: string|null;
  attendanceDurationMinutes?: number;
  trainingGroups: TrainingGroupModel[];
  isUnknownUser?: boolean;
  eventMemberUnknownId?: string | null;
  isManuallyAdded?: boolean;
  iddTimeMinutes: number;
  initialIddTimeMinutes: number;
}
